import React, { useEffect, useRef, useState } from "react";
import { GoogleGenerativeAI } from "@google/generative-ai";
import Swal from "sweetalert2";



const ChatbotPage = () => {
  const defaultGreeting = "ਸਤਿ ਸ੍ਰੀ ਅਕਾਲ! How may I assist you with understanding Sikh teachings today?";
  const [input, setInput] = useState("");
  const [chatHistory, setChatHistory] = useState([
    { role: "bot", text: defaultGreeting }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const chatBodyRef = useRef(null);
  const recognitionRef = useRef(null);
  useEffect(() => {
    const disableContextMenu = (e) => e.preventDefault();
    document.addEventListener("contextmenu", disableContextMenu);
    return () => document.removeEventListener("contextmenu", disableContextMenu);
    }, []);

  // Scroll chat to bottom when new messages arrive
  const scrollToBottom = () => {
    chatBodyRef.current?.scrollTo({
      top: chatBodyRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  // Setup SpeechRecognition for voice input
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = "en-US";
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.onresult = (e) => setInput(e.results[0][0].transcript);
      recognition.onerror = () => Swal.fire("Error", "Voice recognition error occurred.", "error");
      recognitionRef.current = recognition;
    }
  }, []);

  const handleVoiceInput = () => {
    if (recognitionRef.current) recognitionRef.current.start();
    else Swal.fire("Voice recognition not supported in this browser.");
  };

  // Speak the bot response if voiceEnabled
  const handleSpeak = (text) => {
    if (!voiceEnabled) return;
    if (!("speechSynthesis" in window)) {
      Swal.fire("Not supported", "Speech synthesis not supported in your browser.", "error");
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.98;
    utterance.pitch = 1;
    utterance.volume = 5;
    utterance.lang = "en-IN";

    const selectVoice = (voices) => {
      const v = voices.find(v => v.name.toLowerCase().includes("neural") && v.lang.includes("en-in"))
        || voices.find(v => v.name.toLowerCase().includes("natural") && v.lang.includes("en-in"))
        || voices.find(v => v.lang.includes("en-in"))
        || voices.find(v => v.lang.includes("en-us"));
      if (v) utterance.voice = v;
      window.speechSynthesis.speak(utterance);
    };

    let voices = window.speechSynthesis.getVoices();
    if (!voices.length) {
      window.speechSynthesis.onvoiceschanged = () => selectVoice(window.speechSynthesis.getVoices());
    } else {
      selectVoice(voices);
    }
  };

  // Google AI setup
  const genAI = new GoogleGenerativeAI("AIzaSyCX16x7-wwuOF6_V4iIzCm4ZYmMqs6lM6Q");
  const model = genAI.getGenerativeModel({ model: "gemini-2.0-flash-lite" });
  const generationConfig = {
    temperature: 1,
    topP: 0.95,
    topK: 40,
    maxOutputTokens: 8192,
    responseMimeType: "text/plain",
  };

  // Build prompt for AI
  const generatePrompt = (history) => {
    const langName = "English, Hindi, Punjabi, Urdu, Sanskrit";
    const baseInstruction = `
You are a deeply respectful, wise virtual assistant trained only on the teachings of the Guru Granth Sahib.
Your role is to provide answers that reflect spiritual insights, ethics, and compassion according to Sikhism.

🧠 Use internal chain-of-thought reasoning to determine the correct response based on the Guru Granth Sahib.
📖 When appropriate, provide a short, relevant quote (shabad) from the Guru Granth Sahib to support your answer. Include the Ang (page) number if possible.
🗣️ Always respond in the same language as the user's input: ${langName}.
🛑 If the user's input is disrespectful, reply with calm dignity inspired by Sikh teachings and gently ask them to rephrase.

⚠️ Final Output Format:
- Only Show the Final Answer nothing else at all.
- Do NOT include your internal reasoning.
- If quoting scripture, keep it short and directly relevant.
- Make sure the reply is respectful, spiritually grounded, and actionable.
`.trim();

    const historyLines = history
      .map(({ role, text }) => `${role === "user" ? "User:" : "Assistant:"} ${text}`)
      .join("\n");
    const conversationHistory =
      history.length && history[history.length - 1].role === "user"
        ? `${historyLines}\nAssistant: `
        : historyLines;

    return `${baseInstruction}\n\n${conversationHistory}`;
  };

  // Send message to AI
  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;
    const newUserMessage = { role: 'user', text: input };
    const updatedHistory = [...chatHistory, newUserMessage];
    setChatHistory(updatedHistory);
    setInput("");
    setIsLoading(true);
    try {
      const prompt = generatePrompt(updatedHistory);
      const session = model.startChat({ generationConfig, history: [{ role: 'user', parts: [{ text: prompt }] }] });
      const result = await session.sendMessage("");
      const botText = result.response.text();
      setChatHistory(prev => [...prev, { role: 'bot', text: botText }]);
      handleSpeak(botText);
    } catch (e) {
      console.error(e);
      setChatHistory(prev => [...prev, { role: 'bot', text: 'Sorry, an error occurred. Please try again later.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear chat history
  const handleClearChat = () => {
    setChatHistory([{ role: "bot", text: defaultGreeting }]);
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSendMessage();
  };

  return (
    <main className="min-h-screen bg-amber-50 flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 shadow-lg border-b-4 border-blue-900">
        <div className="container mx-auto flex items-center justify-between px-3 sm:px-6">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <img
              src="/Ekonkar.jpg"
              alt="Logo"
              className="h-8 w-8 sm:h-12 sm:w-12 rounded-full border-2 border-yellow-300"
            />
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Eternal Guru</h1>
              <p className="text-xs md:text-sm text-amber-200">
                Wisdom from Guru Granth Sahib
              </p>
            </div>
          </div>
          <div className="hidden md:flex space-x-2">
            {['ਸਤਿ','ਸੰਤੋਖ','ਦਇਆ','ਧਰਮ','ਹਿੰਮਤ'].map((w,i) => (
              <span
                key={i}
                className="bg-amber-100/30 px-3 py-1 text-xs rounded-full hover:bg-amber-100"
              >
                {w}
              </span>
            ))}
          </div>
        </div>
      </header>

      {/* Main Section */}
      <section className="container mx-auto px-2 sm:px-4 py-2 sm:py-4 max-w-4xl flex-1 flex flex-col">
        {/* Intro Card */}
        <div className="bg-white p-3 sm:p-6 rounded-lg shadow border-l-4 border-orange-600 mb-3 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-orange-700 mb-2">ਵਾਹਿਗੁਰੂ ਜੀ ਕਾ ਖਾਲਸਾ, ਵਾਹਿਗੁਰੂ ਜੀ ਕੀ ਫਤਹਿ</h2>
          <p className="text-sm sm:text-base text-gray-700">
            Welcome to Eternal Guru, your guide to the wisdom of Sri Guru Granth Sahib Ji. Ask questions about Sikh teachings, philosophy, history, or practices to receive guidance based on Gurbani and Sikh traditions.
          </p>
          <div className="mt-3 sm:mt-4 flex flex-wrap gap-1 sm:gap-2">
            {['What are the Five Ks?','Explain Mool Mantar','What is Langar?','Teachings on equality'].map((t,i)=>(
              <button
                key={i}
                onClick={() => setInput(t)}
                className="bg-orange-100 text-orange-800 px-2 sm:px-3 py-1 rounded-full hover:bg-orange-200 text-xs sm:text-sm"
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Chat Container */}
        <div className="bg-white border border-orange-200 rounded-lg shadow-md flex-1 flex flex-col overflow-hidden">
          {/* Chat Header */}
          <div className="border-b border-orange-200 bg-orange-50 p-2 sm:p-3 flex flex-wrap justify-between items-center">
            <div className="flex items-center space-x-2 sm:space-x-4 mb-1 sm:mb-0">
              <h3 className="text-sm sm:text-base text-orange-800 font-medium">Conversation with Eternal Guru</h3>
              {/* Toggle Button for Voice Response */}
              <button
                onClick={() => setVoiceEnabled(!voiceEnabled)}
                className={`px-2 py-1 text-xs rounded-full focus:outline-none ${voiceEnabled ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-800'}`}>
                Voice: {voiceEnabled ? 'On' : 'Off'}
              </button>
            </div>
            <button onClick={handleClearChat} className="text-orange-600 text-xs sm:text-sm hover:underline">Clear Chat</button>
          </div>

          <div ref={chatBodyRef} className="p-2 sm:p-4 overflow-y-auto flex-1 space-y-3 sm:space-y-4 max-h-[50vh] sm:max-h-[400px]">
            {chatHistory.map((m,i)=>(
              <div key={i} className={`flex items-start ${m.role==='user'? 'justify-end':'justify-start'}`}>
                {m.role==='bot' && (
                  <img src="/Ekonkar.jpg" alt="Bot" className="h-6 w-6 rounded-full mr-2" />
                )}
                <div className={`inline-block px-3 py-2 rounded-lg text-xs sm:text-sm whitespace-pre-wrap max-w-[85%] sm:max-w-[75%] ${m.role==='user'? 'bg-blue-900 text-white':'bg-orange-50 text-gray-800 border border-orange-200'}`}>{m.text}</div>
              </div>
            ))}
            {isLoading && (
              <div className="flex items-start space-x-2">
                <img
                  src="/Ekonkar.jpg"
                  alt="Bot"
                  className="h-6 w-6 rounded-full"
                />
                <div className="bg-orange-50 text-gray-800 border border-orange-200 inline-block px-3 py-2 rounded-lg text-xs sm:text-sm">
                  <div className="flex items-center space-x-1">
                    <span
                      className="inline-block w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0ms" }}
                    />
                    <span
                      className="inline-block w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "150ms" }}
                    />
                    <span
                      className="inline-block w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "300ms" }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-orange-200 bg-orange-50 p-2 sm:p-4">
            <div className="flex gap-1 sm:gap-2">
              <input
                type="text"
                value={input}
                onChange={e=>setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about Sikh teachings..."
                className="flex-1 px-2 sm:px-4 py-2 text-sm border border-orange-200 rounded focus:ring-orange-500"
                disabled={isLoading}
              />
              <button onClick={handleVoiceInput} className="rounded px-2 sm:px-4 py-2 bg-orange-300 hover:bg-orange-400 text-white text-xs sm:text-sm">🎤</button>
              <button onClick={handleSendMessage} disabled={isLoading||!input.trim()} className={`rounded px-2 sm:px-4 py-2 text-white text-xs sm:text-sm ${isLoading||!input.trim()?'bg-orange-300':'bg-orange-600 hover:bg-orange-700'}`}>Send</button>
            </div>
            <p className="text-xs text-center text-gray-500 mt-2">Ask questions about Sikh philosophy, history, practices, or Gurbani</p>
          </div>
        </div>
      </section>

      <footer className="bg-blue-900 text-white text-center py-4 sm:py-6 mt-auto">
        <div className="container mx-auto px-2 sm:px-4">
          <div className="flex justify-center space-x-4 sm:space-x-6 mb-3 sm:mb-4">
            {['Truth','Contentment','Compassion'].map((label,i)=>(
              <div key={i} className="flex flex-col items-center">
                <span className="text-xs uppercase text-amber-200 mb-1">{label}</span>
                <span className="text-base sm:text-lg font-semibold">{['ਸਤਿ','ਸੰਤੋਖ','ਦਇਆ'][i]}</span>
              </div>
            ))}
          </div>
          <p className="text-xs sm:text-sm">Eternal Guru – Guidance based on the teachings of Sri Guru Granth Sahib Ji</p>
          <p className="text-xs text-amber-200 mt-2">This is an AI assistant. For authoritative guidance, please consult with Giani or visit your local Gurdwara.</p>
          <p className="mt-2 sm:mt-3 text-xs text-amber-200 flex flex-wrap items-center justify-center">
            Powered by:
            <a
              href="https://uktech.ac.in/en"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mx-1"
            >
              <img
                src="https://uktech.ac.in/images/logo.png"
                alt="UTU Logo"
                className="h-4 w-4"
              />
            </a>
            <span className="w-full sm:w-auto mt-1 sm:mt-0">Veer Madho Singh Bhandari Uttarakhand Technical University Dehradun</span>
          </p>
          <p className="mt-2 text-xs text-amber-200 text-center">
            Developed by:{" "}
            <a
              href="https://www.linkedin.com/in/prof-vishal-kumar/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-amber-100"
            >
              Dr. Vishal Kumar
            </a>
            , Vinamra Sharma & Arushi Saklani
          </p>
        </div>
      </footer>
    </main>
  );
};
export default ChatbotPage;